import { useTranslation } from "react-i18next";
import { headlineStatus, summarisePosts } from "@marketing/shared";
import { Badge } from "@/components/ui";

// =============================================================================
// How a campaign or a monthly plan is actually doing.
//
// Both carry a `status` column set when the batch was generated, which never
// moves again. A plan whose posts had all been approved went on reading
// "waiting for approval" — not merely a stale label, but the screen asserting
// something untrue about work the owner had just finished.
//
// So the headline and the counts are derived from the posts every time they
// are rendered. A derived number cannot go stale, and there is no rolled-up
// column to keep in step with every approval, skip and publish.
// =============================================================================

const TONES = {
  waiting: "warning",
  approved: "info",
  published: "success",
  failed: "danger",
  cancelled: "neutral",
  draft: "neutral",
  generating: "info",
  empty: "neutral",
} as const;

export function BatchProgress({
  posts,
  storedStatus,
}: {
  posts: Array<{ status: string }>;
  storedStatus: string;
}) {
  const { t } = useTranslation();
  const progress = summarisePosts(posts.map((p) => p.status));
  const headline = headlineStatus(progress, storedStatus);

  // Only the counts that are actually non-zero, in the order someone reads
  // them: what still needs them first.
  const parts = [
    progress.waiting > 0 ? t("batch.waiting", { count: progress.waiting }) : null,
    progress.approved > 0 ? t("batch.approved", { count: progress.approved }) : null,
    progress.published > 0 ? t("batch.published", { count: progress.published }) : null,
    progress.skipped > 0 ? t("batch.skipped", { count: progress.skipped }) : null,
    progress.failed > 0 ? t("batch.failed", { count: progress.failed }) : null,
  ].filter(Boolean);

  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <Badge tone={TONES[headline]}>{t(`batch.headline.${headline}`)}</Badge>
      {parts.length > 0 ? (
        <span className="text-xs text-muted">{parts.join(" · ")}</span>
      ) : null}
    </span>
  );
}
